import { useState, useRef } from 'react';
import {
  AudioManager,
  AudioPlayerState,
  useAudio,
} from '../../Context/AudioContext';
import { Section, Paragraph, PlaybackMarker } from '../Types/ArticleTypes';

interface UseReadAloudResult {
  // State
  playbackState: AudioPlayerState;
  playbackParagraph: Paragraph | undefined;
  playbackMarker: PlaybackMarker | undefined;
  isReadyForPlayback: boolean;
  audioManager: AudioManager;

  // Actions
  playReadAloud: (startFromParagraph?: Paragraph) => void;
  pauseReadAloud: () => void;
  stopReadAloud: () => void;

  // Event handlers for ArticleContent
  changePlaybackState: (state: AudioPlayerState) => void;
  changePlaybackParagraph: (paragraph: Paragraph) => void;
  changePlaybackMarker: (marker: PlaybackMarker) => void;
  setReadyForPlayback: () => void;
}

export function useArticleReadAloud(
  sections: Section[],
  paragraphs: Paragraph[],
): UseReadAloudResult {
  const { audioManager } = useAudio();
  const [playbackState, setPlaybackState] = useState<AudioPlayerState>(
    AudioPlayerState.Stop,
  );
  const [playbackParagraph, setPlaybackParagraph] = useState<Paragraph>();
  const playbackParagraphRef = useRef<Paragraph>();
  playbackParagraphRef.current = playbackParagraph;
  const [playbackMarker, setPlaybackMarker] = useState<PlaybackMarker>();
  const [isReadyForPlayback, setIsReadyForPlayback] = useState(false);

  const play = (startFromParagraph?: Paragraph) => {
    if (startFromParagraph) {
      const indexOfCurrentSection = sections.indexOf(
        startFromParagraph.section,
      );
      if (sections[indexOfCurrentSection].isBlurred) {
        setPlaybackParagraph(paragraphs[0]);
      } else {
        setPlaybackParagraph(startFromParagraph);
      }
    } else if (playbackParagraph) {
      const indexOfCurrentSection = sections.indexOf(playbackParagraph.section);
      if (sections[indexOfCurrentSection].isBlurred) {
        setPlaybackParagraph(paragraphs[0]);
      }
    } else {
      setPlaybackParagraph(paragraphs[0]);
    }

    setPlaybackState(AudioPlayerState.Play);
    setIsReadyForPlayback(false);
  };

  const pause = () => {
    setPlaybackState(AudioPlayerState.Pause);
  };

  const stop = () => {
    setPlaybackState(AudioPlayerState.Stop);
    // Force state update to happen in separate render cycles
    setTimeout(() => {
      setPlaybackParagraph(undefined);
      setPlaybackMarker(undefined);
    }, 0);
  };

  return {
    // State
    playbackState,
    playbackParagraph,
    playbackMarker,
    isReadyForPlayback,
    audioManager,

    // Actions
    playReadAloud: play,
    pauseReadAloud: pause,
    stopReadAloud: stop,

    // Event handlers
    changePlaybackState: setPlaybackState,
    changePlaybackParagraph: setPlaybackParagraph,
    changePlaybackMarker: setPlaybackMarker,
    setReadyForPlayback: () => setIsReadyForPlayback(true),
  };
}
